import {
	Feature,
	Product,
	getFeatureAvailableFromState,
	getProductAvailableFromState,
	getSoftDeployAccessFromState
} from 'App/components/hooks/featureHelper';
import type { RootState } from 'Store/index';
import type { AgentPageNavSection } from 'Resources/AgentPages';

// The navbar's sections are exactly the sections a Page can be placed under, so
// this is an alias rather than a second copy of the list (WEB-9222).
export type NavSection = AgentPageNavSection;

type App = RootState['App'];

/**
 * Which top-level navbar sections this account and user actually get. The navbar
 * renders a section only when its predicate holds, so a plain CRM account sees
 * `sale` + `followup` and nothing else.
 *
 * Extracted so it has exactly one home: the navbar asks it before pushing a
 * section, and the sidebar asks it to decide whether a navTab Page's section
 * exists — a page under a section this account never renders would otherwise be
 * reachable only by its URL. Keep in step with `Navbar.render`, which owns each
 * section's *default state* but no longer its visibility rule (WEB-9222).
 *
 * Both callers only ask whether a section is present. The returned order mirrors
 * the navbar's left-to-right order for readability, but nothing depends on it —
 * `Navbar.render` pushes its sections in its own sequence.
 */
export const getVisibleNavSections = (
	accountSelf: App['accountSelf'],
	self: App['self'],
	accessRights: App['accessRights'],
	// The user's own workspace grant (WEB-6701), which only the permissions request can
	// answer. Pass false while it is in flight so the section never flashes at a user
	// without access; an administrator does not need it.
	workspaceGranted: boolean
): NavSection[] => {
	if (!accountSelf || !self) return [];

	const features = accountSelf.features || {};
	const isAdmin = self.administrator;

	const hasWorkspaceAccess =
		getFeatureAvailableFromState(accountSelf, Feature.AI_ASSISTANT) ||
		getSoftDeployAccessFromState(accountSelf, 'AI_ASSISTANT');
	const hasEventAccess = accessRights.SocialEvent && getFeatureAvailableFromState(accountSelf, Feature.SOCIAL_EVENTS);

	const visible: NavSection[] = [];
	if (
		getSoftDeployAccessFromState(accountSelf, 'AI_WORKSPACE_PAGE') &&
		hasWorkspaceAccess &&
		(isAdmin || workspaceGranted)
	) {
		visible.push('ai');
	}
	if (accountSelf.products?.crm || isAdmin) {
		visible.push('sale');
	}
	if (
		getProductAvailableFromState(accountSelf, Product.MA) &&
		(isAdmin || self.userParams.mailAdmin || hasEventAccess)
	) {
		visible.push('market');
	}
	if (features.reports || features.looker) {
		visible.push('followup');
	}
	if ((isAdmin || self.support) && getFeatureAvailableFromState(accountSelf, Feature.CUSTOMER_SUPPORT)) {
		visible.push('support');
	}
	return visible;
};
